import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ForumAPI, getApiBaseUrl } from '../utils/api';
import { getToken } from '../utils/auth';
import ConfirmModal from '../components/ConfirmModal';
import { FaArrowLeft, FaComment, FaPaperPlane, FaLock } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import SpinnerLoading from '../components/SpinnerLoading';
import ForumPost from '../components/ForumPost';
import RoleBadge from '../components/RoleBadge';
import VerifiedBadge from '../components/VerifiedBadge';

const ForumPostDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [news, setNews] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [currentUserId, setCurrentUserId] = useState<string>('');
    const [userRole, setUserRole] = useState<string>('');
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

    // Helper to process avatar/image URLs
    const getFullImageUrl = (path: string | null | undefined) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;

        const API_URL = getApiBaseUrl();
        let baseUrl = API_URL.split('/api')[0];
        if (baseUrl.endsWith('/index.php')) {
            baseUrl = baseUrl.replace('/index.php', '');
        }
        baseUrl = baseUrl.replace(/\/$/, '');

        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${baseUrl}${cleanPath}`;
    };

    const rootComments = React.useMemo(() => {
        if (!news?.comments) return [];
        const map = new Map();
        const roots: any[] = [];
        // Deep copy to avoid mutation issues
        const comments = news.comments.map((c: any) => ({ ...c, replies: [] }));

        comments.forEach((c: any) => map.set(c.id, c));

        comments.forEach((c: any) => {
            if (c.parentId && map.has(c.parentId)) {
                map.get(c.parentId).replies.push(c);
            } else {
                roots.push(c);
            }
        });
        return roots;
    }, [news?.comments]);

    useEffect(() => {
        loadNews();
        getCurrentUser();
    }, [id]);

    const getCurrentUser = async () => {
        try {
            const token = getToken();
            if (!token) return;
            const { AuthAPI } = await import('../utils/api');
            const userRes = await AuthAPI.me(token);
            setCurrentUserId(userRes.user.id);
            setUserRole(userRes.user.role || 'user');
            setCurrentUser(userRes.user);
        } catch (error) {
            console.error('Get user error:', error);
        }
    };

    const loadNews = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token || !id) {
                navigate('/login');
                return;
            }

            // Cache busting handled by backend headers now.
            const response = await ForumAPI.getById(id, token);

            // Process URLs in response
            if (response.author && response.author.avatar) {
                response.author.avatar = getFullImageUrl(response.author.avatar);
            }
            if (response.image) {
                response.image = getFullImageUrl(response.image);
            }
            if (response.comments) {
                response.comments = response.comments.map((comment: any) => ({
                    ...comment,
                    user: {
                        ...comment.user,
                        avatar: getFullImageUrl(comment.user?.avatar)
                    }
                }));
            }

            setNews(response);
        } catch (error: any) {
            console.error('Load news error:', error);
            toast.error('Không thể tải tin tức');
            navigate('/forum');
        } finally {
            setLoading(false);
        }
    };

    const handleLikeMainPost = async () => {
        try {
            const token = getToken();
            if (!token || !id) return;

            if (news.liked) {
                await ForumAPI.unlikeNews(id, token);
                setNews((prev: any) => ({ ...prev, liked: false, likeCount: prev.likeCount - 1 }));
            } else {
                await ForumAPI.toggleLike(id, token);
                setNews((prev: any) => ({ ...prev, liked: true, likeCount: prev.likeCount + 1 }));
            }
        } catch (error) {
            console.error('Like error:', error);
            toast.error('Không thể thích bài viết');
        }
    };

    const handleMainPostReply = async (targetId: string, content: string) => {
        if (!content.trim()) return;

        try {
            setSubmitting(true);
            const token = getToken();
            if (!token || !id) return;

            const response = await ForumAPI.addComment(id, content, token);

            const newComment = {
                ...response.comment,
                user: {
                    ...response.comment.user,
                    avatar: getFullImageUrl(response.comment.user?.avatar)
                }
            };

            setNews((prev: any) => ({
                ...prev,
                comments: [...prev.comments, newComment],
                commentCount: prev.commentCount + 1
            }));
            toast.success('Đã thêm bình luận');
        } catch (error) {
            console.error('Add comment error:', error);
            toast.error('Không thể thêm bình luận');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReplySubmit = async (parentId: string, content: string) => {
        try {
            const token = getToken();
            if (!token) {
                toast.error('Vui lòng đăng nhập để phản hồi');
                return;
            }

            const response = await ForumAPI.addComment(id!, content, token, parentId);

            const newReply = {
                id: response.comment.id,
                content: response.comment.content,
                createdAt: response.comment.createdAt,
                updatedAt: response.comment.updatedAt,
                user: {
                    ...response.comment.user,
                    avatar: getFullImageUrl(response.comment.user?.avatar)
                },
                parentId: parentId,
                replies: []
            };

            setNews((prev: any) => ({
                ...prev,
                comments: [...prev.comments, newReply],
                commentCount: prev.commentCount + 1
            }));
            toast.success('Đã gửi phản hồi');
        } catch (error) {
            console.error('Reply error:', error);
            toast.error('Không thể gửi phản hồi. Vui lòng thử lại.');
        }
    };

    const handleUpdateComment = async (commentId: string, newContent: string) => {
        try {
            const token = getToken();
            if (!token) return;

            await ForumAPI.updateComment(commentId, newContent, token);

            setNews((prev: any) => ({
                ...prev,
                comments: prev.comments.map((c: any) =>
                    c.id === commentId ? { ...c, content: newContent } : c
                )
            }));
            toast.success('Đã cập nhật bình luận');
        } catch (error) {
            console.error('Update comment error:', error);
            toast.error('Không thể cập nhật bình luận');
            throw error;
        }
    };

    const handleLikeComment = async (commentId: string) => {
        const token = getToken();
        if (!token) {
            toast.error('Vui lòng đăng nhập');
            return;
        }
        try {
            // Optimistic update
            setNews((prev: any) => ({
                ...prev,
                comments: prev.comments.map((c: any) => {
                    if (c.id === commentId) {
                        const isLiked = !c.isLiked;
                        return {
                            ...c,
                            isLiked,
                            likesCount: isLiked ? (c.likesCount || 0) + 1 : (c.likesCount || 1) - 1
                        };
                    }
                    return c;
                })
            }));

            const { ForumAPI } = await import('../utils/api');
            const res = await ForumAPI.toggleLikeComment(commentId, token);

            // Real update
            setNews((prev: any) => ({
                ...prev,
                comments: prev.comments.map((c: any) =>
                    c.id === commentId ? { ...c, isLiked: res.liked, likesCount: res.likes_count } : c
                )
            }));
        } catch (error) {
            toast.error('Lỗi khi thích bình luận');
            loadNews(); // Revert on error
        }
    };

    const handleDeleteClick = (itemId: string, isMain: boolean = false) => {
        if (isMain) {
            setShowDeleteModal(true);
        } else {
            setCommentToDelete(itemId);
        }
    };



    const confirmDeleteComment = async () => {
        if (!commentToDelete) return;

        try {
            const token = getToken();
            if (!token) return;

            await ForumAPI.removeComment(commentToDelete, token);

            setNews((prev: any) => {
                if (!prev.comments) return prev;
                // Recursive function to find all descendant IDs
                const getDescendantIds = (parentId: string, allComments: any[]): string[] => {
                    const children = allComments.filter(c => c.parentId === parentId);
                    let ids = children.map(c => c.id);
                    children.forEach(child => {
                        ids = [...ids, ...getDescendantIds(child.id, allComments)];
                    });
                    return ids;
                };

                const idsToRemove = [commentToDelete, ...getDescendantIds(commentToDelete, prev.comments)];

                return {
                    ...prev,
                    comments: prev.comments.filter((c: any) => !idsToRemove.includes(c.id)),
                    commentCount: prev.commentCount - idsToRemove.length
                };
            });
            toast.success('Đã xóa bình luận');
        } catch (error) {
            console.error('Delete comment error:', error);
            toast.error('Không thể xóa bình luận');
        } finally {
            setCommentToDelete(null);
        }
    };

    const confirmDeleteMainPost = async () => {
        setShowDeleteModal(false);
        try {
            const token = getToken();
            if (!token || !id) return;
            await ForumAPI.remove(id, token);
            toast.success('Đã xóa bài viết');
            navigate('/forum');
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Không thể xóa bài viết');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="scale-50 transform">
                    <SpinnerLoading />
                </div>
            </div>
        );
    }

    if (!news) return null;

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 font-sans pb-12">

            {/* Simple Header */}
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-md sticky top-0 z-30">
                {/* Decorative Background Elements (Hero Style) */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay pointer-events-none"></div>
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>

                <div className="relative z-10 w-full mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => {
                                const category = searchParams.get('category') || news?.categorySlug || news?.category;
                                navigate(category ? `/forum?category=${category}` : '/forum');
                            }}
                            className="p-2 -ml-2 text-blue-100 hover:text-white rounded-full hover:bg-white/10 transition-all"
                        >
                            <FaArrowLeft />
                        </button>
                        <div>
                            <h1 className="text-lg sm:text-xl font-bold text-white line-clamp-1">
                                {news.title}
                            </h1>
                            <div className="text-xs text-blue-100 flex items-center gap-2">
                                {news.isPinned && <span>📌 Đã ghim</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">

                {/* Main Post Row */}
                <ForumPost
                    id={news.id}
                    isMainPost={true}
                    content={news.content}
                    createdAt={news.createdAt}
                    updatedAt={news.updatedAt}
                    user={news.author}
                    image={news.image}
                    likesCount={news.likeCount}
                    isLiked={news.liked}
                    currentUserId={currentUserId}
                    userRole={userRole}
                    // Actions
                    onDelete={() => handleDeleteClick(news.id, true)}
                    editLink={`/forum/edit/${news.id}`}
                    onLike={handleLikeMainPost}
                    onReply={handleMainPostReply}
                />

                {/* Comments Divider */}
                <div className="flex items-center gap-4 my-8">
                    <div className="h-px bg-gray-300 dark:bg-gray-700 flex-1"></div>
                    <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                        Danh sách phản hồi ({news.commentCount})
                    </span>
                    <div className="h-px bg-gray-300 dark:bg-gray-700 flex-1"></div>
                </div>

                {/* Comments List */}
                <div className="space-y-6">
                    {rootComments.map((comment: any) => (
                        <ForumPost
                            key={comment.id}
                            {...comment}
                            user={comment.user}
                            currentUserId={currentUserId}
                            userRole={userRole}
                            onDelete={(id) => handleDeleteClick(id, false)}
                            onEdit={handleUpdateComment}
                            onReply={handleReplySubmit}
                            onLike={handleLikeComment}
                            replies={comment.replies}
                        />
                    ))}

                    {rootComments.length === 0 && (
                        <div className="text-center py-10 opacity-60 italic text-gray-500">
                            Chưa có bình luận nào.
                        </div>
                    )}
                </div>
                {/* Comment Box removed as per user request (Main Post reply button used instead) */}
            </div>

            {/* Modals */}
            < ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteMainPost}
                title="Xác nhận xóa bài viết"
                message="Bạn có chắc chắn muốn xóa bài viết này không? Hành động này không thể hoàn tác."
                confirmText="Xóa bài viết"
                cancelText="Hủy"
                type="danger"
            />
            <ConfirmModal
                isOpen={!!commentToDelete}
                onClose={() => setCommentToDelete(null)}
                onConfirm={confirmDeleteComment}
                title="Xóa bình luận"
                message="Bạn có chắc chắn muốn xóa bình luận này?"
                confirmText="Xóa"
                cancelText="Hủy"
                type="danger"
            />
        </div >
    );
};

export default ForumPostDetailPage;
